import { FunctionComponent, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MedicationIcon from '@mui/icons-material/Medication';
import MoneyIcon from '@mui/icons-material/Money';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CircularProgress from '@mui/material/CircularProgress';
import { searchService } from '../../../api';
import { useApiCall } from '../../../hooks/useApiCall';
import styles from './AISearchResults.module.css';

interface CarePlan {
  id: string;
  title: string;
  description: string;
  condition: string;
  duration: string;
  estimatedCost: {
    min: number;
    max: number;
  };
  providerCount: number;
  medicationCount: number;
  procedureCount: number;
  successRate: number;
  keyBenefits: string[];
  recommendationScore: number;
  tags: string[];
}

const AISearchResults: FunctionComponent = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [carePlans, setCarePlans] = useState<CarePlan[]>([]);
  
  const query = searchParams.get('q') || '';
  
  const { loading, error, execute: fetchCarePlans } = useApiCall(() => 
    searchService.getAICarePlans(query)
  );

  useEffect(() => {
    const loadCarePlans = async () => {
      if (query) {
        const result = await fetchCarePlans();
        if (result?.success && result.data) {
          setCarePlans(result.data);
        }
      }
    };

    loadCarePlans();
  }, [query]);

  const renderCarePlanCard = (plan: CarePlan) => (
    <div 
      className={styles.carePlanCard}
      onClick={() => navigate(`/ai-care-plan/${plan.id}`)}
    >
      <div className={styles.cardHeader}>
        <div className={styles.planIcon}>
          <HealthAndSafetyIcon />
        </div>
        <div className={styles.planInfo}>
          <h3 className={styles.planTitle}>{plan.title}</h3>
          <p className={styles.planCondition}>{plan.condition}</p>
        </div>
        <div className={styles.recommendationBadge}>
          <AutoAwesomeIcon className={styles.aiIcon} />
          <span>{plan.recommendationScore}% Match</span>
        </div>
      </div>

      <p className={styles.planDescription}>{plan.description}</p>

      <div className={styles.planStats}>
        <div className={styles.statItem}>
          <CalendarMonthIcon className={styles.statIcon} />
          <span>{plan.duration}</span>
        </div>
        <div className={styles.statItem}>
          <MoneyIcon className={styles.statIcon} />
          <span>${plan.estimatedCost.min.toLocaleString()}-${plan.estimatedCost.max.toLocaleString()}</span>
        </div>
        <div className={styles.statItem}>
          <TrendingUpIcon className={styles.statIcon} />
          <span>{plan.successRate}% Success</span>
        </div>
      </div>

      <div className={styles.planResources}>
        <div className={styles.resourceItem}>
          <LocalHospitalIcon className={styles.resourceIcon} />
          <span>{plan.providerCount} Providers</span>
        </div>
        <div className={styles.resourceItem}>
          <MedicationIcon className={styles.resourceIcon} />
          <span>{plan.medicationCount} Medications</span>
        </div>
        <div className={styles.resourceItem}>
          <HealthAndSafetyIcon className={styles.resourceIcon} />
          <span>{plan.procedureCount} Procedures</span>
        </div>
      </div>

      <div className={styles.keyBenefits}>
        <h4 className={styles.benefitsTitle}>Key Benefits:</h4>
        <ul className={styles.benefitsList}>
          {plan.keyBenefits.map((benefit, index) => (
            <li key={index}>{benefit}</li>
          ))}
        </ul>
      </div>

      <div className={styles.planTags}>
        {plan.tags.map((tag, index) => (
          <span key={index} className={styles.tag}>{tag}</span>
        ))}
      </div>

      <button className={styles.viewPlanButton}>
        View Care Plan
        <ChevronRightIcon className={styles.chevronIcon} />
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className={styles.aiSearchResults}>
        <div className={styles.loading}>
          <CircularProgress size={40} />
          <p style={{ marginTop: '16px' }}>Mario AI is analyzing your health needs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.aiSearchResults}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </button>
          <h1 className={styles.headerTitle}>AI Care Plans</h1>
        </div>
        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
          <p>Unable to load care plans. Please try again later.</p>
          <button 
            onClick={() => window.location.reload()} 
            style={{ marginTop: '16px', padding: '8px 16px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.aiSearchResults}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </button>
        <h1 className={styles.headerTitle}>AI Care Plans</h1>
      </div>

      <div className={styles.aiMessage}>
        <AutoAwesomeIcon className={styles.aiMessageIcon} />
        <div className={styles.aiMessageContent}>
          <h2>Personalized Care Plans for "{query}"</h2>
          <p>Mario AI has analyzed your query and created comprehensive care plans tailored to your needs.</p>
        </div>
        <button 
          className={styles.refineButton}
          onClick={() => navigate(`/ai-followup?q=${encodeURIComponent(query)}&context=symptoms`)}
        >
          Answer a few questions for better results
        </button>
      </div>

      <div className={styles.resultsContainer}>
        <div className={styles.resultsHeader}>
          <h3 className={styles.resultsTitle}>Recommended Care Plans</h3>
          <span className={styles.resultsCount}>{carePlans.length} plans found</span>
        </div>

        <div className={styles.carePlansGrid}>
          {carePlans.length > 0 ? (
            carePlans.map((plan) => (
              <div key={plan.id}>
                {renderCarePlanCard(plan)}
              </div>
            ))
          ) : (
            <div style={{ gridColumn: '1 / -1', padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              <p>No care plans found for "{query}". Try refining your search or answer a few questions for better results.</p>
            </div>
          )}
        </div>
      </div>

      <div className={styles.disclaimer}>
        <p>These care plans are AI-generated recommendations based on your query. Please consult with healthcare providers before making any medical decisions.</p>
      </div>
    </div>
  );
};

export default AISearchResults;